import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect
} from 'react';
import apiService from '../services/api';
import {
  getOrCreateGuestId,
  readOnboardingComplete,
  readStoredSubscription,
  subscriptionIsActive,
  writeOnboardingComplete,
  writeStoredSubscription
} from '../utils/guestSession';

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

function mergeRemoteSubscription(remote, guestId) {
  if (!remote?.expiresAt) return null;

  const local = readStoredSubscription();
  const remoteExpires = Number(remote.expiresAt);
  const localExpires = Number(local?.expiresAt || 0);

  if (remoteExpires <= Date.now()) {
    if (local && subscriptionIsActive(local)) {
      writeStoredSubscription(null);
      return null;
    }
    return local;
  }

  if (local && localExpires >= remoteExpires && subscriptionIsActive(local)) {
    return local;
  }

  return writeStoredSubscription({
    plan: remote.plan,
    activatedAt: remote.activatedAt || Date.now(),
    expiresAt: remoteExpires,
    amount: remote.amount,
    currency: remote.currency,
    sessionId: remote.sessionId,
    provider: remote.provider || 'stripe',
    guestId
  });
}

export const SubscriptionProvider = ({ children }) => {
  const [onboardingComplete, setOnboardingComplete] = useState(
    () => readOnboardingComplete()
  );
  const [subscription, setSubscription] = useState(() => readStoredSubscription());
  const [booting, setBooting] = useState(true);
  const guestId = getOrCreateGuestId();

  const withTimeout = (promise, timeoutMs) =>
    new Promise((resolve, reject) => {
      const t = setTimeout(() => reject(new Error('timeout')), timeoutMs);
      promise
        .then((v) => {
          clearTimeout(t);
          resolve(v);
        })
        .catch((e) => {
          clearTimeout(t);
          reject(e);
        });
    });

  const refreshLocalSubscription = useCallback(() => {
    const next = readStoredSubscription();
    setSubscription(next);
    return next;
  }, []);

  useEffect(() => {
    if (readOnboardingComplete() && !onboardingComplete) {
      setOnboardingComplete(true);
    }
  }, [onboardingComplete]);

  useEffect(() => {
    let cancelled = false;

    const syncFromServer = async () => {
      try {
        const id = getOrCreateGuestId();
        // Не блокируем UI надолго из-за проблем backend/сети.
        // Если запрос не успел — полагаемся на localStorage-кэш.
        const result = await withTimeout(
          apiService.fetchSubscriptionStatus(id),
          2500
        );
        if (cancelled) return;

        const merged = mergeRemoteSubscription(result?.subscription, id);
        if (merged) {
          setSubscription(merged);
          writeOnboardingComplete();
          setOnboardingComplete(true);
        } else if (result?.hasAccess === false) {
          const local = readStoredSubscription();
          if (local && !subscriptionIsActive(local)) {
            setSubscription(local);
          } else if (local && subscriptionIsActive(local)) {
            setSubscription(null);
          }
        }
      } catch {
        // Offline or API down — rely on local cache
      } finally {
        if (!cancelled) setBooting(false);
      }
    };

    syncFromServer();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const tick = () => refreshLocalSubscription();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [refreshLocalSubscription]);

  const completeOnboarding = useCallback(() => {
    writeOnboardingComplete();
    setOnboardingComplete(true);
  }, []);

  const startCheckout = useCallback(async (plan = 'membership') => {
    const id = getOrCreateGuestId();
    const result = await apiService.createCheckoutSession(plan || 'membership', id);
    if (!result?.url) {
      throw new Error(result?.message || 'Stripe checkout URL missing');
    }
    window.location.assign(result.url);
    return result;
  }, []);

  const activateFromStripeSession = useCallback(async (sessionId) => {
    if (!sessionId) throw new Error('Missing Stripe session');

    const id = getOrCreateGuestId();
    const result = await apiService.verifyCheckoutSession(sessionId, id);
    const sub = result?.subscription;
    if (!result?.ok || !sub?.plan || !sub?.expiresAt) {
      throw new Error(result?.message || 'Could not verify payment');
    }

    const payload = writeStoredSubscription({
      plan: sub.plan,
      activatedAt: sub.activatedAt || Date.now(),
      expiresAt: sub.expiresAt,
      amount: sub.amount,
      currency: sub.currency,
      sessionId: sub.sessionId || sessionId,
      provider: 'stripe',
      guestId: id
    });

    setSubscription(payload);
    writeOnboardingComplete();
    setOnboardingComplete(true);
    return payload;
  }, []);

  const hasAccess = subscriptionIsActive(subscription);

  const value = useMemo(
    () => ({
      onboardingComplete,
      completeOnboarding,
      subscription,
      hasAccess,
      guestId,
      booting,
      startCheckout,
      activateFromStripeSession,
      refreshLocalSubscription
    }),
    [
      onboardingComplete,
      completeOnboarding,
      subscription,
      hasAccess,
      guestId,
      booting,
      startCheckout,
      activateFromStripeSession,
      refreshLocalSubscription
    ]
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
